import React, { useState, useCallback, useEffect, useRef } from "react";
import useInterval from "use-interval";
import Context from "./index";
import { getPendingOrders } from "utils/Utils";
import { UTILS } from "utils";
import { post } from "api/api/axiosSetup";

const AUDIO_URL =
  "https://uapp-live-com.s3.amazonaws.com/UAPP-MEDIA/PENDING_ORDER_MUSIC.mpeg";

const LiveOrderProvider = (props) => {
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const playingRef = useRef(false);

  useEffect(() => {
    audioRef.current = new Audio(AUDIO_URL);
    audioRef.current.preload = "auto";
    audioRef.current.loop = false;
  }, []);

  const unlockAudio = useCallback(async () => {
    if (!audioRef.current || unlockedRef.current) return;
    try {
      await audioRef.current.play();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      unlockedRef.current = true;
    } catch (e) {}
  }, []);

  useEffect(() => {
    const handler = () => unlockAudio();
    window.addEventListener("touchstart", handler, { once: true });
    window.addEventListener("click", handler, { once: true });
    return () => {
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("click", handler);
    };
  }, [unlockAudio]);

  const returnPendingOrdersCount = async () => {
    const userDetail = UTILS.getCurrentUser();
    if (!userDetail?.token || userDetail?.userType === "STREAM-ROOM-ACCESS")
      return 0;

    try {
      const res = await post("order/getAllPendingOrdersCount", {
        token: userDetail.token,
      });

      const count = getPendingOrders(res, "PENDING");
      setPendingOrdersCount(count);
      return count;
    } catch (error) {
      return 0;
    }
  };

  const updateTitle = useCallback((count) => {
    if (process.env.REACT_APP_TITLE) {
      document.title = count
        ? `(${count}) ${process.env.REACT_APP_TITLE}`
        : process.env.REACT_APP_TITLE;
    } else {
      document.title = count ? `(${count}) Dashboard` : "Dashboard";
    }
  }, []);

  const playAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!unlockedRef.current) return;
    if (playingRef.current) return;

    try {
      playingRef.current = true;
      audio.currentTime = 0;
      await audio.play();
      audio.onended = () => {
        playingRef.current = false;
      };
    } catch (e) {
      playingRef.current = false;
    }
  }, []);

  useInterval(() => {
    (async () => {
      const count = await returnPendingOrdersCount();
      updateTitle(count);
      if (count > 0) playAudio();
    })();
  }, 5000);

  const contextValue = {
    pendingOrdersCount,
    refreshPendingOrders: returnPendingOrdersCount,
    unlockAudio,
    isSoundUnlocked: unlockedRef.current,
  };

  return (
    <Context.Provider value={contextValue}>
      {props.children}
    </Context.Provider>
  );
};

export default LiveOrderProvider;
